/**
 * AuthContext — SECURITY-S4 unified-surface migration.
 *
 * Reads & writes go through the canonical unified surface:
 *   - localStorage.treatx_unified_user  (JSON: { id, email, name, role, ... })
 *   - localStorage.treatx_unified_token
 *
 * The legacy `treatx_user` key (with the deprecated `user_type` shape)
 * is no longer read or written.  Any pre-existing legacy entry is
 * actively purged on mount.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import StorageService from '../services/StorageService';

const AuthContext = createContext(null);

const UNIFIED_USER_KEY = 'treatx_unified_user';
const UNIFIED_TOKEN_KEY = 'treatx_unified_token';

// Project the canonical unified user shape down to the legacy
// AuthContext consumer shape (id / person_id / email / name / role).
// We do NOT introduce a new field; only re-expose what's already there.
const projectFromUnified = (raw) => {
  if (!raw) return null;
  try {
    const u = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!u || !u.email) return null;
    return {
      id: u.id || u.sub,
      person_id: u.person_id || u.id || u.sub,
      email: u.email,
      name: u.name || '',
      role: u.role,
      phone: u.phone || null,
    };
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // SECURITY-S4: purge any pre-existing legacy mirrors on mount.
    localStorage.removeItem('treatx_user');
    const raw = localStorage.getItem(UNIFIED_USER_KEY);
    setUser(projectFromUnified(raw));
    setLoading(false);
  }, []);

  // Called by UnifiedLoginPage after a successful login.  The unified
  // keys are written by UnifiedLoginPage itself; here we just sync state.
  const login = (userData) => {
    setUser(userData);
  };

  const logout = () => {
    setUser(null);
    // FIND-H-08 · fire the backend logout to expire the HttpOnly auth
    // cookie + CSRF cookie. Idempotent, CSRF-exempt. This is the ONLY
    // logout-endpoint fire point on the header-button path; the
    // `unifiedAuth.clearUnifiedAuth()` helper (used by stale-session
    // reactive paths) fires its own copy but is guarded from being
    // triggered by this function since we do not invoke it here.
    try {
      if (typeof fetch !== 'undefined') {
        fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/auth/logout`, {
          method: 'POST', credentials: 'include',
        }).catch(() => { /* noop */ });
      }
    } catch { /* noop */ }
    // Canonical surface — user snapshot only (token is HttpOnly cookie).
    localStorage.removeItem(UNIFIED_USER_KEY);
    localStorage.removeItem(UNIFIED_TOKEN_KEY);
    localStorage.removeItem('treatx_rbac_permissions');
    localStorage.removeItem('treatx_rbac_role_assignments');
    // OF-001C identity-isolation hardening — workspace catalogue + last
    // active workspace MUST be cleared on logout, otherwise the next
    // user's session reads stale workspace/clinic context.
    localStorage.removeItem('treatx_rbac_workspaces');
    localStorage.removeItem('treatx_last_active_workspace');
    localStorage.removeItem('treatx_workspace_selection_provenance');
    sessionStorage.removeItem('treatx_active_clinic_id');
    sessionStorage.removeItem('treatx_clinic_user');
    // Sweep any legacy mirrors (defence-in-depth)
    localStorage.removeItem('treatx_user');
    localStorage.removeItem('treatx_coordinator_token');
    localStorage.removeItem('treatx_coordinator_user');
    localStorage.removeItem('clinicRepresentative');
    // O84-C — identity-scoped CRM caches (patients, leads, offers, clinic
    // profile) must not survive into the next user's session in this tab.
    StorageService.clearIdentityScopedCaches();
    localStorage.removeItem('treatx_admin');
    // OF-001C — broadcast identity-changed so every identity-bound
    // surface (PendingInvitationsBanner, RBACContext, ClinicDataContext,
    // workspace switcher) resets its in-memory React state.  Without
    // this, p61's invites/workspaces remained in the banner's local
    // state and leaked into p62's session after re-login.
    try { window.dispatchEvent(new Event('treatx:identity-changed')); } catch { /* ignore */ }
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    // Mirror back to the canonical unified surface so the next mount
    // picks up the change (no separate legacy key).
    const existing = localStorage.getItem(UNIFIED_USER_KEY);
    if (existing) {
      try {
        const u = JSON.parse(existing);
        localStorage.setItem(
          UNIFIED_USER_KEY,
          JSON.stringify({ ...u, ...updates }),
        );
      } catch {
        /* ignore corrupt entry */
      }
    }
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, loading, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
